/**
 * Attachment resolution for chunked samples: event chunks are materialized
 * with their `attachment://<index>` refs substituted before anything
 * downstream (decode walk, renderers) sees them — the UI never sees a ref
 * (design/large-samples.md, "Data-loading architecture"). Resolution is
 * per-chunk: refs are collected from the parsed chunk, the covering
 * attachment chunks fetched, and the existing `resolveAttachments`
 * substitution applied. ModelEvent `input_refs` message ranges are NOT
 * resolved here — they stay lazy (Confounder 1: the last event references
 * essentially the whole conversation).
 */
import { normalizeEvent } from "@tsmono/inspect-common/normalize";

import { resolveAttachments } from "../utils/attachments";

import {
  SequenceReader,
  type ChunkedEvent,
  type ChunkedSample,
} from "./chunked";
import { log } from "./chunked/log";

const ATTACHMENT_PROTOCOL = "attachment://";
const CONTENT_PROTOCOL = "tc://";

const collectRefs = (value: unknown, into: Set<number>): void => {
  if (typeof value === "string") {
    const ref = value.startsWith(CONTENT_PROTOCOL)
      ? value.replace(CONTENT_PROTOCOL, ATTACHMENT_PROTOCOL)
      : value;
    if (ref.startsWith(ATTACHMENT_PROTOCOL)) {
      const suffix = ref.slice(ATTACHMENT_PROTOCOL.length);
      // strict: resolveAttachments substitutes by the literal suffix, so
      // non-canonical forms ("1e2", "0x10", "-2") would fetch under a
      // different key than they substitute by (and -2 would index from the
      // sequence end) — treat them as unresolvable instead
      if (/^\d+$/.test(suffix)) {
        into.add(Number(suffix));
      }
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      collectRefs(item, into);
    }
    return;
  }
  if (value !== null && typeof value === "object") {
    for (const item of Object.values(value)) {
      collectRefs(item, into);
    }
  }
};

/**
 * Substitute every attachment ref in `items` from the sample's attachments
 * sequence (fetches dedup through the shared chunk-byte store). `label`
 * names the batch in the read log.
 */
export const withAttachmentsResolved = async <T>(
  items: T[],
  chunked: ChunkedSample,
  label: string
): Promise<T[]> => {
  const refs = new Set<number>();
  collectRefs(items, refs);
  if (refs.size === 0) {
    return items;
  }
  log.info(
    `resolve ${refs.size} attachment ref${refs.size === 1 ? "" : "s"} for ${label}`
  );
  const attachments: Record<string, string> = {};
  await Promise.all(
    [...refs].map(async (index) => {
      const [content] = await chunked.attachments.getRange(index, index + 1);
      if (content !== undefined) {
        attachments[String(index)] = content;
      }
    })
  );
  return items.map((item) => resolveAttachments(item, attachments));
};

/**
 * The sample's events reader with boundary normalization (#555) applied and
 * attachment refs resolved per chunk. Chunk-level caching means each chunk
 * normalizes and resolves once.
 *
 * Per-entry (not normalizeEvents): SequenceReader's index math derives from
 * fixed chunk starts, so the transform must be count-preserving — a dropped
 * entry would silently shift every later ordinal. Un-event-shaped entries
 * (impossible from our own chunker) pass through unchanged rather than
 * being dropped.
 */
export const resolvedEventsReader = (
  chunked: ChunkedSample
): SequenceReader<ChunkedEvent> =>
  chunked.events.withTransform((items, start) =>
    withAttachmentsResolved(
      items.map((item) => normalizeEvent(item) ?? item),
      chunked,
      `events chunk ${start}`
    )
  );
